import { useState, useEffect } from 'react';
import Joyride, { type CallBackProps, STATUS } from 'react-joyride';
import { useTranslation } from 'react-i18next';

const TOUR_KEY = 'infoscend.tour.completed.v1';

export function useTour() {
  const [tourActive, setTourActive] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_KEY);
    if (!completed) {
      setTourActive(true);
    }
    setHasCompleted(!!completed);
  }, []);

  function completeTour() {
    localStorage.setItem(TOUR_KEY, new Date().toISOString());
    setTourActive(false);
    setHasCompleted(true);
  }

  function retakeTour() {
    setTourActive(true);
  }

  return { tourActive, hasCompleted, completeTour, retakeTour, setTourActive };
}

interface TourProps {
  tourActive: boolean;
  onFinish: () => void;
}

export function BrandApproverTour({ tourActive, onFinish }: TourProps) {
  const { t } = useTranslation();

  const steps = [
    {
      target: '[data-tour="kpi-strip"]',
      title: t('tour.brand.step1.title', { defaultValue: 'Your Daily Performance Strip' }),
      content: t('tour.brand.step1.body', {
        defaultValue:
          "Each morning starts here. Auto-processed today is your settlement velocity, minutes saved is the people-cost story, FTE projected is the headline you'll repeat in finance meetings.",
      }),
      disableBeacon: true,
    },
    {
      target: '[data-tour="awaiting-review-card"]',
      title: t('tour.brand.step2.title', { defaultValue: 'Claims Awaiting Review' }),
      content: t('tour.brand.step2.body', {
        defaultValue:
          'These are the claims that need your judgment. The queue is sorted by SLA risk and override flags first.',
      }),
    },
    {
      target: '[data-tour="auto-approved-card"]',
      title: t('tour.brand.step3.title', { defaultValue: 'Auto-Approved Today' }),
      content: t('tour.brand.step3.body', {
        defaultValue:
          "Click to spot-check. Every auto-approval is reversible — see the override button on each claim.",
      }),
    },
    {
      target: '[data-tour="ai-proposal-card"]',
      title: t('tour.brand.step4.title', { defaultValue: 'AI Verification Summary' }),
      content: t('tour.brand.step4.body', {
        defaultValue:
          "The AI shows verdict, confidence, and reasoning. You're not approving the AI — you're approving the claim, with the AI as a second opinion.",
      }),
      disableBeacon: true,
    },
    {
      target: '[data-tour="decision-action-bar"]',
      title: t('tour.brand.step5.title', { defaultValue: 'Decision Panel' }),
      content: t('tour.brand.step5.body', {
        defaultValue:
          'Approve, modify the amount, reject, or request more evidence. Every decision creates an audit trail.',
      }),
    },
  ];

  function handleCallback(data: CallBackProps) {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      onFinish();
    }
  }

  return (
    <Joyride
      steps={steps}
      run={tourActive}
      continuous
      showProgress
      showSkipButton
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: '#A86A2C',
          backgroundColor: '#FAF8F2',
          textColor: '#1A1209',
          zIndex: 1000,
        },
      }}
    />
  );
}

export function DistributorTour({ tourActive, onFinish }: TourProps) {
  const { t } = useTranslation();

  const steps = [
    {
      target: '[data-tour="new-claim-button"]',
      title: t('tour.distributor.step1.title', { defaultValue: 'Submit a New Claim' }),
      content: t('tour.distributor.step1.body', {
        defaultValue:
          'All submissions start here. The wizard is one screen — fill details, drop your documents into the upload zone, and review the AI verification before submitting.',
      }),
      disableBeacon: true,
    },
    {
      target: '[data-tour="claims-list"]',
      title: t('tour.distributor.step2.title', { defaultValue: 'Your Claims' }),
      content: t('tour.distributor.step2.body', {
        defaultValue:
          "Your historical submissions. The status badge shows your view; the approver sees more detail but the timeline is the same.",
      }),
    },
    {
      target: '[data-tour="status-badge"]',
      title: t('tour.distributor.step3.title', { defaultValue: 'Status Updates' }),
      content: t('tour.distributor.step3.body', {
        defaultValue:
          "PENDING REVIEW means we've received it. The approver decision usually lands within 12 hours during business days.",
      }),
    },
  ];

  function handleCallback(data: CallBackProps) {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      onFinish();
    }
  }

  return (
    <Joyride
      steps={steps}
      run={tourActive}
      continuous
      showProgress
      showSkipButton
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: '#A86A2C',
          backgroundColor: '#FAF8F2',
          textColor: '#1A1209',
          zIndex: 1000,
        },
      }}
    />
  );
}
